import React from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  Box,
  type Omittable,
  Icon,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
  Separator,
  Text,
} from "@salestrak/ui";

import {
  ThemeAppearanceCards,
  ThemeCatalogField,
} from "#app/components/theme/theme-appearance-controls";

// ---------------------------------------------------------------------------
// Shared theme picker content (hosted by the sub-menu popup)
// ---------------------------------------------------------------------------

// The one menu that keeps its own width: the preview card, the two selects
// and the custom editor's rows all compose against it, and the popup must
// not reflow when the editor appears. Every other menu takes a `MenuPopup
// width` rung instead of a number.
const POPUP_WIDTH = 320;

/**
 * The theme picker. It opens with the OUTCOME — a preview card painted from
 * the resolved theme + accent, inset on the popup's own padding so it reads
 * as a framed specimen rather than the popup wearing the theme — over two
 * label-above groups: the appearance cards and the SAME Interface-theme
 * select as the settings row, swatch pill included (the pill previews the
 * OPTION under the cursor while browsing; the card previews the committed
 * state — different jobs). A plain "Theme" title tops the column — a name
 * only, never a toolbar: no trailing actions ride it. One rhythm
 * throughout: md insets, md between sections, xs from a label to its
 * control. The palette-shaping controls stay in settings (see the inline
 * note below); the footer row is the door there.
 */
export function ThemePickerContent({
  onNavigate,
}: Omittable<{ onNavigate?: () => void }> = {}): React.ReactElement {
  const navigate = useNavigate();
  const { workspace } = useParams({ strict: false }) as { workspace: string };

  return (
    <Box direction="column">
      <Box direction="column" gap="md" padding="md">
        <Text size="base" weight="semibold">
          Theme
        </Text>

        {/* The two decisions a viewer makes in passing: which appearance, and
            which palette. Everything that shapes a palette — accent,
            background, foreground, contrast — lives in settings, where there
            is room to see both halves at once. A popup that carries the whole
            editor asks someone switching to dark to walk past six controls. */}
        <ThemeAppearanceCards compact />

        <ThemeCatalogField width="full" />
      </Box>

      {/* The way to everything this popup deliberately does not carry. A
          full-bleed footer row under its own rule: the menu grammar for
          leaving, and the popup's largest single target. */}
      <Separator />
      <Box
        align="center"
        as="button"
        cursor="pointer"
        direction="row"
        gap="sm"
        hoverBackground
        onClick={() => {
          // Close the host BEFORE navigating, or the menu is still floating
          // over the screen it just sent you to. The row is a plain pressable
          // rather than a MenuItem on purpose — this content has to stay
          // mountable outside a menu — so the host hands down its own close.
          onNavigate?.();
          navigate({
            params: { workspace },
            to: "/$workspace/settings/appearance",
          });
        }}
        paddingX="md"
        paddingY="base"
        type="button"
      >
        <Icon color="muted" name="sliders-horizontal" size="sm" />
        <Text size="sm" variant="muted">
          Appearance settings
        </Text>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ThemeSubMenu — hover sub-menu hosting the picker
// ---------------------------------------------------------------------------

export function ThemeSubMenu({
  onNavigate,
}: Omittable<{ onNavigate?: () => void }> = {}): React.ReactElement {
  return (
    <MenuSub>
      <MenuSubTrigger>
        <Icon name="sun" size="md" color="muted" />
        <Text>Theme</Text>
      </MenuSubTrigger>
      <MenuSubPopup padding="none">
        {/* The popup part carries the radius; this box clips to it so the
            full-bleed footer row's hover wash cannot poke square corners
            past the rounded bottom edge. */}
        <Box overflow="hidden" rounded="lg" width={POPUP_WIDTH}>
          <ThemePickerContent onNavigate={onNavigate} />
        </Box>
      </MenuSubPopup>
    </MenuSub>
  );
}
